import { SetRequired } from 'type-fest';

import { reportException } from '_lib/utils/sentryService';

import {
  CreateResource,
  DeleteResource,
  GetByIdResource,
  PatchHook,
  PutResource,
  ResourceId,
  UnpagedListResource,
  createApiResource,
  queryKeysAreEqual,
  useCustomMutation
} from '../apiResourceFactory';
import { API } from '../apiService';
import {
  AccountCreateDTO,
  AccountQueryParams,
  AccountReactivateDTO,
  AccountResponseDTO,
  AccountUpdateDTO
} from '../apiTypes.shim';

import { ourQueryClient } from '../QueryProvider';

import { companyResourceUri } from './companyResource';

interface AccountResource
  extends UnpagedListResource<AccountResponseDTO, AccountQueryParams>,
    GetByIdResource<AccountResponseDTO>,
    CreateResource<AccountCreateDTO, AccountResponseDTO>,
    PutResource<AccountUpdateDTO, AccountResponseDTO>,
    DeleteResource {
  unpagedListNameOnly: UnpagedListResource<
    Pick<AccountResponseDTO, 'id' | 'name'>,
    SetRequired<AccountQueryParams, 'serializer'>
  >['unpagedList'];
  useReactivateWithCompanies: () => ReturnType<PatchHook<AccountReactivateDTO, AccountResponseDTO>>;
}

export const createAccountResource = (): AccountResource => {
  const resourceUri = `account`;

  const {
    unpagedList,
    useUnpagedList,
    unpagedListQueryKey,
    getById,
    useGetById,
    getByIdQueryKey,
    create,
    useCreate,
    put,
    usePut,
    deleteItem,
    useDelete
  } = createApiResource(API, resourceUri);

  type ReactivateWithCompaniesVariables = { id: ResourceId; data: AccountReactivateDTO };
  const useReactivateWithCompanies = () => {
    const context = useCustomMutation<AccountResponseDTO, unknown, ReactivateWithCompaniesVariables>({
      mutationFn: async ({ id, data }) => {
        const response = await API.patch(`${resourceUri}/${id}/reactivate`, data);
        return response.data;
      },
      onSuccess: (data, { id }) => {
        ourQueryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === resourceUri && !queryKeysAreEqual(query.queryKey, getByIdQueryKey({ id })),
          refetchType: 'active'
        });
        ourQueryClient.invalidateQueries({ queryKey: [companyResourceUri], refetchType: 'active' });
        ourQueryClient.setQueryData(getByIdQueryKey<AccountResponseDTO>({ id }), data);
      },
      onError: (error) => reportException(error)
    });

    return { ...context, patch: context.mutateAsync };
  };

  return {
    unpagedList,
    useUnpagedList,
    unpagedListQueryKey,
    unpagedListNameOnly: unpagedList,
    getById,
    useGetById,
    getByIdQueryKey,
    create,
    useCreate,
    put,
    usePut,
    deleteItem,
    useDelete: (mutationConfig) => {
      const context = useDelete({
        onSuccess: (_data, { id }) => {
          ourQueryClient.invalidateQueries({ queryKey: [resourceUri], refetchType: 'active' });
          ourQueryClient.invalidateQueries({ queryKey: [companyResourceUri], refetchType: 'active' });
          ourQueryClient.removeQueries({ queryKey: getByIdQueryKey({ id }) });
        },
        ...mutationConfig
      });
      return { ...context, deleteItem: context.mutateAsync };
    },
    useReactivateWithCompanies
  };
};
